import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminResources, adminCreateResource, adminUpdateResource, adminDeleteResource, adminUpload, apiError,
} from "@/lib/api";
import { AdminHeader, PrimaryBtn, Modal, fieldCls, labelCls, ImageHint } from "./ui";
import { DocUploadField } from "./DocUploadField";

const EMPTY = { titulo: "", autor: "", portada: "", descripcion: "", archivo: "", archivo_pdf: "", archivo_pdf_size: 0, archivo_epub: "", archivo_epub_size: 0, activo: true };

export default function AdminResources() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [up, setUp] = useState("");
  const [docBusy, setDocBusy] = useState(0);
  const busy = (b) => setDocBusy((n) => n + (b ? 1 : -1));
  const setDoc = (k) => (url, size) => setForm((f) => ({ ...f, [`archivo_${k}`]: url, [`archivo_${k}_size`]: size }));

  const load = () => adminResources().then(setItems);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const upload = (field) => async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUp(field);
    try { const res = await adminUpload(file); setForm((f) => ({ ...f, [field]: res.url })); toast.success("Archivo subido."); }
    catch (err) { toast.error(apiError(err)); } finally { setUp(""); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await adminUpdateResource(editing.id, form); toast.success("Recurso actualizado."); }
      else { await adminCreateResource(form); toast.success("Recurso creado."); }
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const remove = async (r) => { if (!window.confirm(`¿Eliminar "${r.titulo}"?`)) return; await adminDeleteResource(r.id); toast.success("Eliminado."); load(); };

  return (
    <div>
      <AdminHeader title="Gratis" subtitle={`${items.length} material(es)`} action={<PrimaryBtn onClick={openNew} testId="new-resource-btn"><Plus size={15} /> Nuevo material</PrimaryBtn>} />
      {items.length === 0 ? (
        <div className="rounded-xl bg-white border border-dashed border-[#0f2544]/15 py-16 text-center text-[#8a8a83]">
          Aún no hay materiales gratuitos. Añade uno con su imagen y su enlace de descarga.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((r) => (
            <div key={r.id} className="rounded-xl bg-white border border-[#0f2544]/8 p-4" data-testid={`resource-row-${r.slug}`}>
              <div className="flex gap-3">
                <div className="w-12 h-16 bg-[#e7e5df] rounded overflow-hidden shrink-0">{r.portada && <img src={r.portada} alt="" className="w-full h-full object-cover" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#0f2544] truncate">{r.titulo}</div>
                  <div className="text-xs text-[#8a8a83]">{r.autor}</div>
                  <div className="text-xs mt-1">{(r.archivo || r.archivo_pdf || r.archivo_epub) ? <span className="text-green-600">{[r.archivo_pdf && "PDF", r.archivo_epub && "EPUB", r.archivo && "Enlace"].filter(Boolean).join(" + ")}</span> : <span className="text-amber-600">Sin archivo</span>} · {r.descargas} descargas</div>
                </div>
              </div>
              <div className="flex gap-1 mt-2 justify-end">
                <button onClick={() => openEdit(r)} className="p-1.5 text-[#8a8a83] hover:text-[#1f44e0]"><Pencil size={14} /></button>
                <button onClick={() => remove(r)} className="p-1.5 text-[#8a8a83] hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar recurso" : "Nuevo recurso"}>
        <form onSubmit={save} className="space-y-4" data-testid="resource-form">
          <div><label className={labelCls}>Título *</label><input required value={form.titulo} onChange={set("titulo")} className={fieldCls} data-testid="resource-titulo" /></div>
          <div><label className={labelCls}>Autor</label><input value={form.autor} onChange={set("autor")} className={fieldCls} /></div>
          <div><label className={labelCls}>Descripción</label><textarea rows={3} value={form.descripcion} onChange={set("descripcion")} className={fieldCls} /></div>
          <div>
            <label className={labelCls}>Portada</label>
            <ImageHint>Medida recomendada: <strong>600 × 800 px</strong> (vertical, relación 3:4). Formato JPG o PNG.</ImageHint>
            <div className="flex items-center gap-3">
              {form.portada && <img src={form.portada} alt="" className="w-10 h-14 object-cover rounded border" />}
              <input type="file" accept="image/*" onChange={upload("portada")} className="text-sm" />
              {up === "portada" && <span className="text-xs text-[#8a8a83]">Subiendo...</span>}
            </div>
          </div>
          <div>
            <label className={labelCls}>Enlace de descarga externo (opcional)</label>
            <input value={form.archivo} onChange={set("archivo")} className={fieldCls} placeholder="https://..." data-testid="resource-download-link" />
            <p className="text-xs text-[#8a8a83] mt-1">Para libros alojados en otro servidor (Google Drive, Dropbox, etc.).</p>
          </div>
          <div className="rounded-xl border border-[#0f2544]/10 p-4 space-y-4">
            <p className="text-xs text-[#8a8a83]">O sube los archivos para que se descarguen desde nuestra web. Puedes subir PDF, EPUB o ambos (máx. 1 GB cada uno).</p>
            <DocUploadField kind="PDF" url={form.archivo_pdf} size={form.archivo_pdf_size} onChange={setDoc("pdf")} onBusy={busy} />
            <DocUploadField kind="EPUB" url={form.archivo_epub} size={form.archivo_epub_size} onChange={setDoc("epub")} onBusy={busy} />
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.activo} onChange={set("activo")} className="accent-[#1f44e0]" /> Activo (visible en la web)</label>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2.5 text-sm text-[#8a8a83]">Cancelar</button>
            <PrimaryBtn type="submit" disabled={saving || docBusy > 0} testId="save-resource-btn">{saving ? "Guardando..." : docBusy > 0 ? "Subiendo archivo..." : "Guardar"}</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
